import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { ArrowLeft } from "lucide-react";
import {
  MapPin,
  Package,
  ArrowDown,
  IndianRupee,
  User,
  Phone,
  Home,
  Hash,
  Truck,
  Clock,
  Box,
  CreditCard,
  Scale,
  Layers,
  List,
  Tag,
} from "lucide-react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import UpdateSenderAdd from "./UpdateSenderAdd";
import UpdateReciverAdd from "./UpdateReciverAdd";
import UpdateOrderDetails from "./UpdateOrderDetails";
import EmployeeAuthModal from "../../employeeAuth/EmployeeAuthModal";
const THEME_GREEN = "#0CBB7D";
const LABEL_STYLE = "uppercase tracking-wider text-[11px] text-gray-400 font-medium";
const VALUE_STYLE_DESKTOP = "text-[13px] text-gray-700";
const VALUE_STYLE_MOBILE = "text-[14px] sm:text-[13px] text-gray-700 font-medium";

const OrderDetails = ({ isSidebarAdmin }) => {
  const [isModalOpen1, setIsModalOpen1] = useState(false);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [isModalOpen3, setIsModalOpen3] = useState(false);
  const [employeeAccess, setEmployeeAccess] = useState({
    isAdmin: false,
    canUpdate: false,
  });
  const [showEmployeeAuthModal, setShowEmployeeAuthModal] = useState(false);
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState([]);
  const [error, setError] = useState("");
  const { id } = useParams();
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  // --- Helpers for capitalization/formatting
  const capitalizeWord = (w) => {
    if (!w || typeof w !== "string") return "";
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  };
  // Capitalize every word of a string
  const titleCase = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((s) => (s ? capitalizeWord(s) : s))
      .join(" ");
  };
  // Ensures first letter after each comma is capitalized. Also ensures each word gets title-cased.
  const capitalizeAfterComma = (input) => {
    if (!input) return "";
    // Split by comma, trim, title case each chunk, then join with comma + space
    return input
      .split(",")
      .map((chunk) => titleCase(chunk.trim()))
      .filter(Boolean)
      .join(", ");
  };
  // Address specific: make first word before first comma capitalized (titleCase) and rest follow comma capitalization
  const formatAddressFull = (address = "") => {
    if (!address) return "";
    const parts = address.split(",");
    // first chunk - title case by words
    parts[0] = titleCase(parts[0].trim());
    // remaining chunks title case
    for (let i = 1; i < parts.length; i++) parts[i] = titleCase(parts[i].trim());
    return parts.map((p) => p).filter(Boolean).join(", ");
  };
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        // simple cookie handling (same as original)
        const [session, token] = document.cookie.split("=");
        if (isSidebarAdmin) {
          setEmployeeAccess({ isAdmin: true, canUpdate: true });
          setShowEmployeeAuthModal(false);
        } else {
          const userInfo = token ? JSON.parse(atob(token.split(".")[1])) : null;
          if (userInfo?.employee && userInfo.employee.isEmployee === true) {
            const employeeResponse = await axios.get(`${REACT_APP_BACKEND_URL}/staffRole/verify`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const employee = employeeResponse.data.employee;
            const canUpdate = !!employee?.accessRights?.orders?.["All Orders"]?.update;
            setEmployeeAccess({ canUpdate });
            if (!canUpdate) setShowEmployeeAuthModal(true);
          } else {
            setEmployeeAccess({ isAdmin: false, canUpdate: true });
            setShowEmployeeAuthModal(false);
          }
        }
        const response = await axios.get(`${REACT_APP_BACKEND_URL}/order/getOrderById/${id}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        // normalize productDetails and packageDetails defaults
        const data = response.data || {};
        data.productDetails = data.productDetails || [];
        data.packageDetails = data.packageDetails || {};
        setOrder(data);
        if (data.shipment_id) {
          try {
            const shiprocketOrderRes = await axios.get(
              `${REACT_APP_BACKEND_URL}/Shiprocket/getOrderDetails/${String(data.shipment_id)}`
            );
            if (shiprocketOrderRes.data) {
              await axios.get(
                `${REACT_APP_BACKEND_URL}/Shiprocket/getCargoTracking/${String(shiprocketOrderRes.data.waybill_no)}`
              );
            }
          } catch (err) {
            // ignore
          }
        }
        const filteredTracking = data.tracking
          ? data.tracking.filter((item) => Object.keys(item).length > 1)
          : [];
        setTracking(filteredTracking);
      } catch (error) {
        setError(error.response?.data?.error || "Failed to fetch order.");
      }
    };
    fetchOrder();
  }, [id, isSidebarAdmin, REACT_APP_BACKEND_URL]);
  // Update handlers (unchanged logic, just kept here)
  const handleUpdateSenderDetails = async (updatedData) => {
    try {
      const [session, token] = document.cookie.split("=");
      await axios.put(
        `${REACT_APP_BACKEND_URL}/order/updateOrder/${id}`,
        { pickupAddress: updatedData },
        { headers: { authorization: `Bearer ${token}` } }
      );
      setOrder((prevOrder) => ({
        ...prevOrder,
        pickupAddress: updatedData,
      }));
      toast.success("Sender details updated successfully");
    } catch (error) {
      toast.error("Failed to update sender details");
    }
  };
  const handleUpdateRecieverDetails = async (updatedRecieverAddress) => {
    try {
      const [session, token] = document.cookie.split("=");
      await axios.put(
        `${REACT_APP_BACKEND_URL}/order/updateOrder/${id}`,
        { receiverAddress: updatedRecieverAddress },
        { headers: { authorization: `Bearer ${token}` } }
      );
      setOrder((prevOrder) => ({
        ...prevOrder,
        receiverAddress: updatedRecieverAddress,
      }));
      toast.success("Receiver details updated successfully");
    } catch (error) {
      toast.error("Failed to update receiver details");
    }
  };
  const handleUpdateOrderDetails = async (updatedOrderData) => {
    try {
      const [session, token] = document.cookie.split("=");
      if (!order.paymentDetails) {
        order.paymentDetails = {};
      }
      const formattedData = {
        paymentDetails: {
          method: updatedOrderData.paymentMethod,
          amount: updatedOrderData.codAmount,
        },
        packageDetails: {
          deadWeight: updatedOrderData.weight,
          volumetricWeight: {
            length: updatedOrderData.length,
            width: updatedOrderData.breadth,
            height: updatedOrderData.height,
          },
          noOfBox: updatedOrderData.noOfBox,
          weightPerBox: updatedOrderData.weightPerBox,
        },
      };
      await axios.put(`${REACT_APP_BACKEND_URL}/order/updateOrder/${id}`, formattedData, {
        headers: { authorization: `Bearer ${token}` },
      });
      // update local UI for immediate reflect
      setOrder((prev) => ({
        ...prev,
        paymentDetails: formattedData.paymentDetails,
        packageDetails: formattedData.packageDetails,
      }));
      toast.success("Order details updated");
    } catch (error) {
      toast.error("Failed to update order details");
    }
  };
  if (error) return <div className="text-red-500">{error}</div>;
  if (!order) return <div>Loading...</div>;
  // Pick first package details if in array format (compatibility)
  const pkg = Array.isArray(order.packageDetails)
    ? order.packageDetails[0]
    : order.packageDetails || {};
  // Calculate small stats
  const totalQuantity = (order.productDetails || []).reduce((sum, p) => sum + Number(p.quantity || 0), 0);
  // Person Section Block
  function PersonBlock({ data, side, onEdit, canEdit }) {
    // Build merged address, city, state as requested
    const rawAddress = data?.address || "";
    const mergedAddressParts = [];
    if (rawAddress) mergedAddressParts.push(formatAddressFull(rawAddress));
    if (data?.city) mergedAddressParts.push(titleCase(String(data.city)));
    if (data?.state) mergedAddressParts.push(titleCase(String(data.state)));
    const mergedAddress = mergedAddressParts.join(", ");
    return (
      <div className="flex flex-col flex-1 min-w-0 bg-white p-4 rounded-xl shadow-sm border border-[#e6f8f3]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* Different icons for from/to */}
            <div className="p-1 rounded bg-[#f0fdfa]">
              {side === "from" ? <Truck size={18} color={THEME_GREEN} /> : <MapPin size={18} color={THEME_GREEN} />}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-gray-600">
                {side === "from" ? "From" : "To"}
              </div>
              <div className="text-[11px] text-gray-400">{side === "from" ? "Pickup Address" : "Delivery Details"}</div>
            </div>
          </div>
          {order.status === "new" ? (
            canEdit ? (
              <button
                onClick={onEdit}
                className="w-8 h-8 flex items-center justify-center rounded bg-[#e6f8f3] hover:bg-[#cfffea] border-0"
                title="Edit"
              >
                <FaEdit size={13} color={THEME_GREEN} />
              </button>
            ) : (
              <button
                onClick={() => setShowEmployeeAuthModal(true)}
                className="w-8 h-8 flex items-center justify-center rounded bg-gray-100"
                title="No permission"
              >
                <FaEdit size={13} color="#bababa" />
              </button>
            )
          ) : null}
        </div>
        {/* Two-column key-value layout. Left column = key (with icon), right column = value. vertical thin divider between columns, horizontal thin lines between rows */}
        <div className="grid grid-cols-1 gap-1 text-sm">
          {/* Row: Contact Name */}
          <div className="flex items-center py-3 border-t first:border-t-0">
            <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
              <User size={16} />
              <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">Contact Name</div>
            </div>
            <div className="flex-1 pl-3 border-l border-[#eef8f4]">
              <div className="text-[15px] text-gray-800 font-medium">
                {data?.contactName ? capitalizeAfterComma(String(data.contactName)) : "--"}
              </div>
            </div>
          </div>
          {/* Row: Phone */}
          <div className="flex items-center py-2 border-t">
            <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
              <Phone size={16} />
              <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">Phone</div>
            </div>
            <div className="flex-1 pl-3 border-l border-[#eef8f4]">
              <div className="text-[15px] text-gray-800">{data?.phoneNumber || "--"}</div>
            </div>
          </div>
          {/* Row: Email */}
          <div className="flex items-center py-2 border-t">
            <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
              <Layers size={16} />
              <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">Email</div>
            </div>
            <div className="flex-1 pl-3 border-l border-[#eef8f4]">
              <div className="text-[15px] text-gray-800">{data?.email || "--"}</div>
            </div>
          </div>
          {/* Row: Address (merged with city and state) */}
          <div className="flex items-start py-2 border-t">
            <div className="w-40 pr-3 flex items-start gap-2 text-[12px] text-gray-500">
              <Home size={16} />
              <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">Address</div>
            </div>
            <div className="flex-1 pl-3 border-l border-[#eef8f4]">
              <div className="text-[15px] text-gray-800">{mergedAddress || "--"}</div>
            </div>
          </div>

          {/* Row: Pincode */}
          <div className="flex items-center py-2 border-t">
            <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
              <Hash size={16} />
              <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">Pincode</div>
            </div>
            <div className="flex-1 pl-3 border-l border-[#eef8f4]">
              <div className="text-[15px] text-gray-800">{data?.pinCode || "--"}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Tracking Block
  function TrackingTimeline() {
    if (!tracking.length)
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="rounded-full bg-gray-100 p-2 mb-1">
              <Clock size={20} color="#bababa" />
            </div>
            <span className="text-[14px] text-gray-400 font-medium mt-1">Tracking details not available</span>
          </div>
        </div>
      );
    return (
      <div className="overflow-y-auto h-full pr-2">
        <ol className="relative border-l-2 border-[#0CBB7D30] pl-4">
          {[...new Map(
            tracking
              .filter((i) => i.Instructions && i.StatusDateTime)
              .reverse()
              .map((i) => [i.StatusDateTime, i])
          ).values()]
            .reverse()
            .map((item, idx) => {
              const dt = new Date(item.StatusDateTime);
              return (
                <li key={idx} className="mb-8 last:mb-0 relative">
                  <div className="absolute -left-6 top-1.5 bg-[#0CBB7D] w-3 h-3 rounded-full border-4 border-white shadow" />
                  <div className="ml-2">
                    <div className="text-[12px] text-gray-500 mb-1">
                      {dt.toLocaleDateString()} {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="bg-[#e6f8f3] px-3 py-2 rounded-lg flex flex-col border-l-4 border-[#0CBB7D] shadow-sm">
                      <div className="text-[14px] text-gray-700 font-medium">{item.Instructions}</div>
                      <div className="text-[12px] text-[#0cbb7d] mt-0.5">{item.StatusLocation}</div>
                    </div>
                  </div>
                </li>
              );
            })}
        </ol>
      </div>
    );
  }
  // Desktop-only small stats cards (to occupy similar width as tracking timeline). Hidden on mobile (sm:hidden => opposite - show on sm+)
  const SmallStat = ({ Icon, label, value }) => (
    <div className="flex-1 min-w-0 bg-white rounded-lg border border-[#e6f8f3] px-3 py-2 shadow-sm flex items-center gap-3">
      <div className="p-2 rounded bg-[#f0fdfa]">
        <Icon size={18} color={THEME_GREEN} />
      </div>
      <div>
        <div className="text-[11px] text-gray-400 uppercase font-medium">{label}</div>
        <div className="text-[14px] text-gray-800 font-medium">{value ?? "--"}</div>
      </div>
    </div>
  );


function ProductSection() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#e6f8f3] px-5 py-4 
      h-auto sm:h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <Box size={18} color={THEME_GREEN} />
        <span className="block text-[15px] text-gray-500 font-semibold">Products</span>
      </div>

      {/* Desktop Table with Scroll */}
      <div className="hidden sm:block overflow-y-auto flex-1">
        <table className="w-full text-left text-[12px] uppercase tracking-wider font-medium">
          <thead className="text-gray-400 sticky top-0 bg-white">
            <tr>
              <th className="py-2 px-2">NAME</th>
              
              <th className="py-2 px-2">PRICE</th>
              <th className="py-2 px-2">SKU</th>
            </tr>
          </thead>
          <tbody>
            {order.productDetails.map((product, idx) => (
              <tr key={idx} className="border-b">
                <td className="py-2 px-2 text-gray-700">{product.name.toUpperCase()}</td>
                
                <td className="py-2 px-2 text-gray-700">₹{product.unitPrice}</td>
                <td className="py-2 px-2 text-gray-700">{product.sku}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile layout (auto height, no scroll) */}
      <div className="sm:hidden flex flex-col">
        {order.productDetails.map((product, idx) => (
          <div key={idx} className="flex flex-col py-3 border-b last:border-b-0">
            {/* Row 1: NAME - VALUE */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[13px] uppercase text-gray-500 font-medium">NAME -</span>
              <span className="text-[13px] text-gray-800">{product.name.toUpperCase()}</span>
            </div>

            {/* Row 2: | PRICE | SKU */}
            <div className="flex justify-between text-[13px] text-gray-600 gap-1">
              
              <div className="flex-1">
                <span className="uppercase text-gray-500 text-[11px]">PRICE:</span>{" "}
                <span className="text-gray-800 text-[11px]">₹{product.unitPrice}</span>
              </div>
              <div className="flex-1">
                <span className="uppercase text-gray-500 font-medium text-[11px]">SKU:</span>{" "}
                <span className="text-gray-800 text-[11px]">{product.sku}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


  return (
    <>
      {/* Fix zoom on mobile: Add viewport meta tag dynamically */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
      />
      <div className="min-h-full w-full px-1 sm:px-2 text-[15px]">
        {/* Topbar */}
        <div className="flex items-center gap-2 pb-2">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full border border-[#e6f8f3] bg-gray-300 hover:bg-gray-400 transition"
            title="Go Back"
          >
            <ArrowLeft size={15} />
          </button>
          <div className="flex items-center gap-1" style={{ marginTop: "1.5px" }}>
            
            <span className="text-[15px] font-semibold text-gray-500">
              Order ID -
            </span>
            <span className="text-[14px] font-semibold text-[#0cbb7d]">
              {order.orderId}
            </span>
          </div>
          <span className="px-1.5 uppercase py-0.3 ml-1 rounded-md text-[10px] font-medium bg-[#e6f8f3] text-[#0cbb7d] border border-[#bbf2dd]">
            {order.status}
          </span>
        </div>
        {/* Main Content */}
        <div className="flex flex-col xl:flex-row gap-2">
          <div className="flex flex-col gap-2 flex-1 min-w-[340px]">
            {/* Desktop-only small stats that align with tracking timeline width */}
            <div className="lg:flex gap-2 grid grid-cols-2">
              <SmallStat Icon={IndianRupee} label="Invoice Value" value={order?.paymentDetails?.amount ? `₹${order.paymentDetails.amount}` : "--"} />
              <SmallStat Icon={CreditCard} label="Payment Mode" value={order?.paymentDetails?.method || "--"} />
              <SmallStat Icon={Truck} label="From" value={order?.pickupAddress?.city ? titleCase(String(order.pickupAddress.city)) : "--"} />
              <SmallStat Icon={MapPin} label="To" value={order?.receiverAddress?.city ? titleCase(String(order.receiverAddress.city)) : "--"} />
              <SmallStat Icon={List} label="No. of Box" value={order?.productDetails[0]?.totalPackages} />
              <SmallStat Icon={Box} label="Total Weight" value={order?.applicableWeight || "--"} />
            </div>
            {/* Addresses */}
            <div className="flex flex-col md:flex-row gap-2">
              <PersonBlock
                data={order.pickupAddress}
                side="from"
                canEdit={isSidebarAdmin || employeeAccess.isAdmin || employeeAccess.canUpdate}
                onEdit={() => setIsModalOpen1(true)}
              />
              <PersonBlock
                data={order.receiverAddress}
                side="to"
                canEdit={isSidebarAdmin || employeeAccess.isAdmin || employeeAccess.canUpdate}
                onEdit={() => setIsModalOpen2(true)}
              />
            </div>

            {/* Tracking Timeline */}
            <div className="">
              <div className="rounded-2xl bg-white border border-[#e6f8f3] shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock size={18} color={THEME_GREEN} />
                    <span className="block text-[15px] text-gray-500 font-semibold">Tracking Timeline</span>
                  </div>
                </div>
                <div
                  className="relative"
                  style={{
                    height: 256,
                    minHeight: 180,
                    maxHeight: 256,
                    overflow: "hidden",
                  }}
                >
                  {/* Scrollable timeline */}
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                      overflowY: "auto",
                    }}
                  >
                    <TrackingTimeline />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right panel: Order Details and Products */}
          <div className="flex flex-col gap-2 w-full xl:max-w-md min-w-[280px]">
            {/* Order Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-[#e6f8f3] px-5 py-5 relative">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <Package size={18} color={THEME_GREEN} />
                  <span className="tblock text-[15px] text-gray-500 font-semibold">Order Details</span>
                </div>
                {order.status === "new" &&
                  (isSidebarAdmin || employeeAccess.isAdmin || employeeAccess.canUpdate ? (
                    <button
                      onClick={() => setIsModalOpen3(true)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-[#e6f8f3] hover:bg-[#cfffea] border-0"
                    >
                      <FaEdit size={13} color={THEME_GREEN} />
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowEmployeeAuthModal(true)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-gray-100"
                      title="No permission"
                    >
                      <FaEdit size={13} color="#bababa" />
                    </button>
                  ))}
              </div>
              <div className="flex flex-col gap-3 text-[14px]">
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><Tag size={14} className="inline-block mr-1" />Order id</span>
                  <span className={VALUE_STYLE_MOBILE}>{order.orderId}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><CreditCard size={14} className="inline-block mr-1" />Payment Mode</span>
                  <span className={VALUE_STYLE_MOBILE}>{order.paymentDetails?.method || "--"}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><IndianRupee size={14} className="inline-block mr-1" />Invoice value</span>
                  <span className={VALUE_STYLE_MOBILE}>₹{order.paymentDetails?.amount || "--"}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><Layers size={14} className="inline-block mr-1" />Dimension</span>
                  <span className={VALUE_STYLE_MOBILE}>
                    {(pkg?.volumetricWeight?.height || "--") +
                      " x " +
                      (pkg?.volumetricWeight?.length || "--") +
                      " x " +
                      (pkg?.volumetricWeight?.width || "--")}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><Scale size={14} className="inline-block mr-1" />Total Weight</span>
                  <span className={VALUE_STYLE_MOBILE}>{order?.applicableWeight || "--"} Kg</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className={LABEL_STYLE}><Box size={14} className="inline-block mr-1" />No. of box</span>
                  <span className={VALUE_STYLE_MOBILE}>{order?.productDetails[0]?.totalPackages || "--"}</span>
                </div>
                
              </div>
            </div>
            <ProductSection />
          </div>
        </div>
        {/* Modals */}
        <UpdateSenderAdd isOpen={isModalOpen1} onClose={() => setIsModalOpen1(false)} onSave={handleUpdateSenderDetails} PickupAddress={order.pickupAddress} />
        <UpdateReciverAdd isOpen={isModalOpen2} onClose={() => setIsModalOpen2(false)} onSave={handleUpdateRecieverDetails} ReceiverAddress={order.receiverAddress} />
        <UpdateOrderDetails isOpen={isModalOpen3} onClose={() => setIsModalOpen3(false)} order={order} handleUpdateOrderDetails={handleUpdateOrderDetails} />
        {!isSidebarAdmin && showEmployeeAuthModal && (
          <EmployeeAuthModal employeeModalShow={showEmployeeAuthModal} employeeModalClose={() => setShowEmployeeAuthModal(false)} />
        )}
      </div>
    </>
  );
};
export default OrderDetails;
